// ISO-8601 calendar dates and instants as Effect Schema, accepting exactly what
// Zod's `z.iso.date()` and `z.iso.datetime()` accept, so values stored by Zod
// code keep decoding. `Iso8601DateTime` decodes to the same branded type as its
// `@finnest/core/schemas/time` Zod schema.
import { Schema } from 'effect';
import type { Iso8601DateTime as Iso8601DateTimeType } from '../../../schemas/time/primitives.ts';

// A calendar day that exists: month lengths and leap years are in the pattern.
const DAY =
  '(?:(?:\\d\\d[2468][048]|\\d\\d[13579][26]|\\d\\d0[48]|[02468][048]00|[13579][26]00)-02-29|\\d{4}-(?:(?:0[13578]|1[02])-(?:0[1-9]|[12]\\d|3[01])|(?:0[469]|11)-(?:0[1-9]|[12]\\d|30)|(?:02)-(?:0[1-9]|1\\d|2[0-8])))';
// RFC 3339 requires seconds wherever the time carries `Z` or an offset.
const TIME = '(?:[01]\\d|2[0-3]):[0-5]\\d:[0-5]\\d(?:\\.\\d+)?';
const OFFSET = '[+-](?:[01]\\d|2[0-3]):[0-5]\\d';

const ISO_DATE = new RegExp(`^${DAY}$`, 'u');
const ISO_DATE_TIME_UTC = new RegExp(`^${DAY}T${TIME}Z$`, 'u');
const ISO_DATE_TIME = new RegExp(`^${DAY}T${TIME}(?:Z|${OFFSET})$`, 'u');

/** A calendar day `YYYY-MM-DD` that exists. */
export const IsoDate = Schema.String.check(
  Schema.isPattern(ISO_DATE, { expected: 'a calendar date YYYY-MM-DD' }),
).annotate({ format: 'date' });
export type IsoDate = typeof IsoDate.Type;

/** An RFC 3339 instant in UTC (`Z`), as `z.iso.datetime()` accepts it. */
export const IsoDateTimeUtc = Schema.String.check(
  Schema.isPattern(ISO_DATE_TIME_UTC, { expected: 'an RFC 3339 UTC date-time' }),
).annotate({ format: 'date-time' });
export type IsoDateTimeUtc = typeof IsoDateTimeUtc.Type;

/** An RFC 3339 instant with `Z` or a numeric offset, as `z.iso.datetime({ offset: true })` accepts it. */
export const IsoDateTime = Schema.String.check(
  Schema.isPattern(ISO_DATE_TIME, { expected: 'an RFC 3339 date-time with offset' }),
).annotate({ format: 'date-time' });
export type IsoDateTime = typeof IsoDateTime.Type;

/** `IsoDateTime`, branded as the frozen Zod `Iso8601DateTime`. */
export const Iso8601DateTime = Schema.String.pipe(
  Schema.refine((value): value is Iso8601DateTimeType => ISO_DATE_TIME.test(value), {
    identifier: 'Iso8601DateTime',
    expected: 'an RFC 3339 date-time with offset',
    toJsonSchema: () => ({ format: 'date-time' }),
  }),
);
export type Iso8601DateTime = typeof Iso8601DateTime.Type;
